import { useCallback, useEffect } from 'react'

import { ReportSection, ResetLink } from 'app/common/templates-next/report-template'
import ReportFilterForm from 'app/common/templates-next/report-template/report-filter-form'
import ReportFilterLayout from 'app/common/templates-next/report-template/report-filter-layout'
import { ID } from 'app/common/ui-next/_filter-compilations/predefined-ids'
import { DetailsBox, detailsBoxID } from 'app/common/ui-next/_filter-compilations/details'
import { useReportPerform } from 'app/dataProviders/reports/hooks'
import { ReportType } from 'app/dataTypes/reports'
import { orgsFilterSelection } from 'app/dataProviders/filter-items'
import { MailingBox, canMailingOnly } from 'app/common/ui-next/_filter-compilations/mailing'
import { useDataBoxSelector } from 'app/common/data-box-store'
import { useUser } from 'app/dataProviders/user/hooks'

import {
  ClassesListFilter,
  CustomerTypeFilter,
  EmployeeListFilter,
  FinancesSummaryGroupFilter,
  OrgTypesFilter,
  OrgsFilter,
  SchoolYearFilter,
  StudentListFilter,
  PeriodFilterUnlimited,
} from '../../common/financesCommonFilter'

import { filterInit, resetToDefaultAll } from '../../common/financesCommonFilter/filterInit'
import { FinancesControlId } from '../../common/financesCommonFilter/types'
import { HideCorrectionPayments } from './filterItems/HideCorrectionPayments'
import styles from './Filters.scss'

const isMailingOnly = (formData: FormData) => !!canMailingOnly(formData)

type Props = {
  onReportId: (reportID: string | null) => void
  profileLink: string
}

export const Filters = ({ onReportId, profileLink }: Props) => {
  const { email, loading: loadingEmail } = useUser()
  const orgsFilterSelectionData = useDataBoxSelector(orgsFilterSelection, () =>
    orgsFilterSelection.getData(),
  )
  const isDisabled = !(
    orgsFilterSelectionData.length === 1 && orgsFilterSelectionData[0] !== 'empty'
  )

  const { perform } = useReportPerform({
    onReportId,
    isMailingOnly,
  })

  const resetAll = useCallback(() => {
    resetToDefaultAll()
  }, [])

  const performWithAdditionalData = useCallback(
    async (formData: FormData) => {
      await perform({ formData, reportType: ReportType.FinancesSummaryReplenishment })
    },
    [perform],
  )

  useEffect(() => {
    return filterInit()
  }, [])

  return (
    <ReportSection
      header="Фильтры"
      headerBar={<ResetLink onClick={resetAll}>{'Сбросить все фильтры'}</ResetLink>}
      resetStickyShift={false}
      skipPadding
    >
      <div className={styles.formContent}>
        <ReportFilterForm
          onSubmit={performWithAdditionalData}
          submitButtonTitle="Сформировать"
          disabled={isDisabled}
        >
          {(pending) => (
            <ReportFilterLayout
              column1={
                <>
                  <OrgTypesFilter id={ID.orgType} pending={pending} />
                  <OrgsFilter id={ID.org} pending={pending} />
                  <CustomerTypeFilter id={ID.customerType} pending={pending} />
                  <ClassesListFilter id={ID.classesList} pending={pending} multiple />
                  <StudentListFilter id={ID.studentList} pending={pending} />
                  <EmployeeListFilter id={ID.employeeList} pending={pending} />
                  <DetailsBox id={ID.details} disabled defaultSelection />
                  <FinancesSummaryGroupFilter id={ID.groupBy} pending={pending} />
                </>
              }
              column2={
                <>
                  <SchoolYearFilter id={ID.schoolYear} pending={pending} />
                  <PeriodFilterUnlimited id={ID.periodDateFormat} pending={pending} />
                  <HideCorrectionPayments
                    id={FinancesControlId.HideCorrectionPayments}
                    pending={pending}
                  />
                  <MailingBox
                    visibleFromBox={detailsBoxID}
                    disabled={pending || loadingEmail}
                    email={email}
                    profileLink={profileLink}
                    defaultVisible
                  />
                </>
              }
            />
          )}
        </ReportFilterForm>
      </div>
    </ReportSection>
  )
}
